import { useMemo } from 'react';
import { LTSSService, StaffMetrics, WeeklyHours, Program } from '../types/ltss';
import { getProgramLimits } from '../components/Settings/ProgramLimits';

// Helper to get the start of the state service week (Thursday)
// State week runs Thursday to Wednesday
function getWeekStart(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun, 1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri, 6=Sat
  // Calculate days since last Thursday
  // If today is Thu (4), offset is 0
  // If today is Fri (5), offset is 1
  // If today is Sat (6), offset is 2
  // If today is Sun (0), offset is 3
  // If today is Mon (1), offset is 4
  // If today is Tue (2), offset is 5
  // If today is Wed (3), offset is 6
  const daysSinceThursday = (day + 3) % 7; // Thu=0, Fri=1, Sat=2, Sun=3, Mon=4, Tue=5, Wed=6
  d.setDate(d.getDate() - daysSinceThursday);
  d.setHours(0, 0, 0, 0);
  return d;
}

// Helper to get the end of the state service week (Wednesday)
function getWeekEnd(date: Date): Date {
  const weekStart = getWeekStart(date);
  const d = new Date(weekStart);
  d.setDate(d.getDate() + 6); // Wednesday is 6 days after Thursday
  d.setHours(23, 59, 59, 999);
  return d;
}

// Format week label for display
function formatWeekLabel(weekStart: Date): string {
  const weekEnd = getWeekEnd(weekStart);
  const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  return `${weekStart.toLocaleDateString('en-US', options)} - ${weekEnd.toLocaleDateString('en-US', options)}`;
}

export function useStaffMetrics(services: LTSSService[]): StaffMetrics[] {
  return useMemo(() => {
    const limits = getProgramLimits();
    const metricsMap: Record<string, StaffMetrics> = {};
    // Track weekly hours per staff: staffName -> weekKey -> { hours, billableHours, services }
    const weeklyMap: Record<string, Record<string, { weekStart: Date; hours: number; billableHours: number; services: number }>> = {};
    // Track daily hours per staff per day for applying daily caps: staffName -> dateKey -> { program, hours }[]
    const dailyMap: Record<string, Record<string, { program: Program; hours: number }[]>> = {};

    // Step 1: Pre-calculate daily totals per staff per day (across ALL clients)
    // This is the key fix - daily cap applies to total hours worked by staff across all clients
    const staffDailyTotals: Record<string, Record<string, { totalHours: number; program: Program }>> = {};
    services.forEach(service => {
      const staffName = service.staffName;
      if (!staffName || !service.serviceDate) return;
      
      const dateKey = new Date(service.serviceDate).toISOString().split('T')[0];
      const serviceHours = service.serviceLength || 0;
      
      if (!staffDailyTotals[staffName]) {
        staffDailyTotals[staffName] = {};
      }
      if (!staffDailyTotals[staffName][dateKey]) {
        staffDailyTotals[staffName][dateKey] = { totalHours: 0, program: service.program };
      }
      staffDailyTotals[staffName][dateKey].totalHours += serviceHours;
    });

    // Step 2: Calculate daily cap factor for each staff-day
    // If staff worked 10 hours but cap is 8, factor = 8/10 = 0.8
    const staffDailyCapFactor: Record<string, Record<string, number>> = {};
    Object.keys(staffDailyTotals).forEach(staffName => {
      staffDailyCapFactor[staffName] = {};
      Object.keys(staffDailyTotals[staffName]).forEach(dateKey => {
        const dayData = staffDailyTotals[staffName][dateKey];
        const programLimit = limits[dayData.program];
        const maxHoursPerDay = programLimit?.maxHoursPerDay || 12;
        
        if (dayData.totalHours > maxHoursPerDay) {
          staffDailyCapFactor[staffName][dateKey] = maxHoursPerDay / dayData.totalHours;
        } else {
          staffDailyCapFactor[staffName][dateKey] = 1; // No cap needed
        }
      });
    });

    // Step 3: Calculate weekly totals per staff-client with daily caps applied
    // staffName -> clientName -> weekKey -> { cappedHours, authorized }
    const staffClientWeekMap: Record<string, Record<string, Record<string, { cappedHours: number; authorized: number }>>> = {};
    
    services.forEach(service => {
      const staffName = service.staffName;
      const clientName = service.clientName;
      if (!staffName || !clientName || !service.serviceDate) return;

      const dateKey = new Date(service.serviceDate).toISOString().split('T')[0];
      const weekStart = getWeekStart(new Date(service.serviceDate));
      const weekKey = weekStart.toISOString();
      const serviceHours = service.serviceLength || 0;
      const authorizedHours = service.weeklyPosHours || 0;
      
      // Apply daily cap factor to this service
      const capFactor = staffDailyCapFactor[staffName]?.[dateKey] || 1;
      const cappedServiceHours = serviceHours * capFactor;

      if (!staffClientWeekMap[staffName]) {
        staffClientWeekMap[staffName] = {};
      }
      if (!staffClientWeekMap[staffName][clientName]) {
        staffClientWeekMap[staffName][clientName] = {};
      }
      if (!staffClientWeekMap[staffName][clientName][weekKey]) {
        staffClientWeekMap[staffName][clientName][weekKey] = {
          cappedHours: 0,
          authorized: authorizedHours,
        };
      }
      
      staffClientWeekMap[staffName][clientName][weekKey].cappedHours += cappedServiceHours;
      
      // Update authorized if we find a higher value
      if (authorizedHours > staffClientWeekMap[staffName][clientName][weekKey].authorized) {
        staffClientWeekMap[staffName][clientName][weekKey].authorized = authorizedHours;
      }
    });

    // Step 4: Calculate weekly authorization cap factor for each staff-client-week
    const staffClientWeekCapFactor: Record<string, Record<string, Record<string, number>>> = {};
    Object.keys(staffClientWeekMap).forEach(staffName => {
      staffClientWeekCapFactor[staffName] = {};
      Object.keys(staffClientWeekMap[staffName]).forEach(clientName => {
        staffClientWeekCapFactor[staffName][clientName] = {};
        Object.keys(staffClientWeekMap[staffName][clientName]).forEach(weekKey => {
          const weekData = staffClientWeekMap[staffName][clientName][weekKey];
          if (weekData.authorized > 0 && weekData.cappedHours > weekData.authorized) {
            staffClientWeekCapFactor[staffName][clientName][weekKey] = weekData.authorized / weekData.cappedHours;
          } else {
            staffClientWeekCapFactor[staffName][clientName][weekKey] = 1; // No cap needed
          }
        });
      });
    });

    // Step 5: Process services and apply both caps
    services.forEach(service => {
      const staffName = service.staffName;
      if (!staffName) return;

      if (!metricsMap[staffName]) {
        metricsMap[staffName] = {
          staffName,
          staffId: service.staffId,
          totalServices: 0,
          totalHours: 0,
          billableHours: 0,
          weeklyHours: [],
          manualCorrections: 0,
          manualCorrectionDates: [],
          missingTimeIncidents: 0,
          cleanClockIns: 0,
          cleanRate: 0,
          penaltyStatus: 'ok',
        };
        weeklyMap[staffName] = {};
        dailyMap[staffName] = {};
      }

      const m = metricsMap[staffName];
      const serviceHours = service.serviceLength || 0;
      const program = service.program;

      m.totalServices++;
      
      // Only count hours if we have a valid service date
      if (service.serviceDate) {
        m.totalHours += serviceHours;
        
        const dateKey = new Date(service.serviceDate).toISOString().split('T')[0];
        const clientName = service.clientName;
        const weekStart = getWeekStart(new Date(service.serviceDate));
        const weekKey = weekStart.toISOString();
        
        if (!dailyMap[staffName][dateKey]) {
          dailyMap[staffName][dateKey] = [];
        }
        dailyMap[staffName][dateKey].push({ program, hours: serviceHours });

        // Apply daily cap factor
        const dailyCapFactor = staffDailyCapFactor[staffName]?.[dateKey] || 1;
        let billableHours = serviceHours * dailyCapFactor;

        // Apply weekly client authorization cap factor
        const weeklyCapFactor = staffClientWeekCapFactor[staffName]?.[clientName]?.[weekKey] || 1;
        billableHours = billableHours * weeklyCapFactor;

        m.billableHours += billableHours;

        // Track weekly hours
        if (!weeklyMap[staffName][weekKey]) {
          weeklyMap[staffName][weekKey] = {
            weekStart,
            hours: 0,
            billableHours: 0,
            services: 0,
          };
        }
        weeklyMap[staffName][weekKey].hours += serviceHours;
        weeklyMap[staffName][weekKey].billableHours += billableHours;
        weeklyMap[staffName][weekKey].services++;
      }

      // Count manual corrections and track dates
      if (service.serviceInitiationSource?.includes('Provider Manual')) {
        m.manualCorrections++;
        if (service.serviceDate) {
          m.manualCorrectionDates.push(service.serviceDate);
        }
      }

      // Count missing time
      if (service.serviceStatus === 'New: Missing Time') {
        m.missingTimeIncidents++;
      }

      // Count clean clock-ins (EVV App or Telephone only, no Provider Manual)
      const source = service.serviceInitiationSource || '';
      if (
        (source === 'Telephone' || source === 'EVV App') &&
        !source.includes('Provider Manual')
      ) {
        m.cleanClockIns++;
      }
    });

    // Convert weekly map to sorted array and calculate rates
    return Object.values(metricsMap).map(m => {
      // Build weekly hours array sorted by date (most recent first)
      const weeklyHours: WeeklyHours[] = Object.values(weeklyMap[m.staffName] || {})
        .map(w => ({
          weekStart: w.weekStart,
          weekEnd: getWeekEnd(w.weekStart),
          weekLabel: formatWeekLabel(w.weekStart),
          hours: w.hours,
          billableHours: w.billableHours,
          services: w.services,
        }))
        .sort((a, b) => b.weekStart.getTime() - a.weekStart.getTime());

      return {
        ...m,
        weeklyHours,
        cleanRate: m.totalServices > 0 
          ? Math.round((m.cleanClockIns / m.totalServices) * 100) 
          : 0,
        penaltyStatus: 
          m.manualCorrections >= 4 ? 'exceeded' :
          m.manualCorrections === 3 ? 'risk' : 'ok',
      };
    });
  }, [services]);
}
